import { Select, Input } from '../../components/ui/Input'
import { Button, IconButton } from '../../components/ui/Button'
import { ModalidadeCartao, MODALIDADE_CARTAO_LABEL } from '../../lib/enums'
import { formatMoney } from '../../lib/utils'
import { MAX_PARTES, valoresDasPartes, type ParteForm } from './divisao'
import type { CartaoUtilizavel } from '../../types'

/** Aviso de saldo/limite, só informativo: a compra é salva mesmo assim. */
function avisoDeSaldo(c: CartaoUtilizavel | undefined, valor: number): string | null {
  if (!c || valor <= 0) return null
  if (c.modalidade === ModalidadeCartao.Credito) {
    if (c.limiteDisponivel != null && valor > c.limiteDisponivel)
      return `Limite disponível de ${c.nome}: ${formatMoney(c.limiteDisponivel)}; a parte passa em ${formatMoney(valor - c.limiteDisponivel)}.`
  } else if (c.saldoDisponivel != null && valor > c.saldoDisponivel) {
    return `Saldo de ${c.nome}: ${formatMoney(c.saldoDisponivel)}; a parte passa em ${formatMoney(valor - c.saldoDisponivel)}.`
  }
  return null
}

function rotuloCartao(c: CartaoUtilizavel): string {
  const disp = c.modalidade === ModalidadeCartao.Credito
    ? (c.limiteDisponivel != null ? ` · limite ${formatMoney(c.limiteDisponivel)}` : '')
    : (c.saldoDisponivel != null ? ` · saldo ${formatMoney(c.saldoDisponivel)}` : '')
  return `${c.nome} · ${MODALIDADE_CARTAO_LABEL[c.modalidade]}${disp}`
}

/** Editor das partes de uma compra dividida. Pronto para n partes: o limite visível vem de MAX_PARTES. */
export function PartesDivisao({ valorTotal, partes, onChange, cartoes }: {
  valorTotal: number; partes: ParteForm[]; onChange: (p: ParteForm[]) => void; cartoes: CartaoUtilizavel[]
}) {
  const valores = valoresDasPartes(valorTotal, partes)
  const meus = cartoes.filter((c) => c.ehMeu)
  const deOutros = cartoes.filter((c) => !c.ehMeu)
  const donos = [...new Set(deOutros.map((c) => c.donoNome))]
  const alterar = (i: number, p: Partial<ParteForm>) => onChange(partes.map((x, k) => (k === i ? { ...x, ...p } : x)))

  return (
    <div className="space-y-3 rounded border border-border/70 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-13 font-medium text-white"><i className="ri-split-cells-horizontal text-info" aria-hidden /> {partes.length} cartões nesta compra</p>
        <span className="text-2xs text-muted">Você informa o valor das primeiras partes; a última recebe o resto.</span>
      </div>
      {partes.map((p, i) => {
        const ultima = i === partes.length - 1
        const c = cartoes.find((x) => x.id === p.cartaoId)
        const aviso = avisoDeSaldo(c, valores[i])
        return (
          <div key={i} className="space-y-2 rounded bg-surface2 p-3">
            <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
              <Select label={`Cartão ${i + 1}`} value={p.cartaoId} onChange={(e) => alterar(i, { cartaoId: e.target.value, numParcelas: '1' })}>
                <option value="">Selecione...</option>
                {meus.length > 0 && <optgroup label="Seus cartões">{meus.map((x) => <option key={x.id} value={x.id}>{rotuloCartao(x)}</option>)}</optgroup>}
                {donos.map((d) => (
                  <optgroup key={d} label={`Cartões de ${d}`}>
                    {deOutros.filter((x) => x.donoNome === d).map((x) => <option key={x.id} value={x.id}>{rotuloCartao(x)}</option>)}
                  </optgroup>
                ))}
              </Select>
              {ultima ? (
                <div>
                  <span className="form-label">O resto fica aqui</span>
                  <p className={`flex h-[38px] items-center rounded border border-border/70 px-3 text-13 ${valores[i] > 0 ? 'text-white' : 'text-expense'}`}>{formatMoney(valores[i])}</p>
                </div>
              ) : (
                <Input label="Valor (R$)" type="number" min={0} step="0.01" value={p.valor} onChange={(e) => alterar(i, { valor: e.target.value })} placeholder="0,00" />
              )}
              {c?.aceitaParcelas ? (
                <Input label="Parcelas" type="number" min={1} max={24} value={p.numParcelas} onChange={(e) => alterar(i, { numParcelas: e.target.value })} />
              ) : (
                <p className="self-end pb-2 text-13 text-muted">{c ? 'À vista' : ''}</p>
              )}
            </div>
            {c && (
              <p className="text-2xs text-muted">
                {c.ehMeu ? 'Seu cartão' : `Cartão de ${c.donoNome}`} · {c.aceitaParcelas ? 'crédito, entra na fatura' : c.modalidade === ModalidadeCartao.Debito ? 'débito, sai da conta na hora' : 'pré-pago, sai do saldo na hora'}
                {c.aceitaParcelas && Number(p.numParcelas) > 1 && valores[i] > 0 ? ` · ${Math.floor(Number(p.numParcelas))}x de ${formatMoney(valores[i] / Math.floor(Number(p.numParcelas)))}` : ''}
              </p>
            )}
            {aviso && <p className="flex items-center gap-1.5 text-13 text-warning"><i className="ri-alert-line" aria-hidden /> {aviso} Você pode salvar mesmo assim.</p>}
            {partes.length > 2 && <IconButton icon="ri-delete-bin-line" label={`Remover cartão ${i + 1}`} onClick={() => onChange(partes.filter((_, k) => k !== i))} />}
          </div>
        )
      })}
      {partes.length < MAX_PARTES && (
        <Button size="sm" variant="light" icon="ri-add-line" onClick={() => onChange([...partes.slice(0, -1), { cartaoId: '', valor: '', numParcelas: '1' }, partes[partes.length - 1]])}>Adicionar cartão</Button>
      )}
    </div>
  )
}
